// The picture of a fraction model: a bar, a circle or a rectangle cut into
// equal parts, some shaded. Plain React SVG, like practice-graphs.tsx.
//
// A shaded part is filled AND carries a dot, and every part has its own
// outline, so shaded and unshaded parts are told apart without colour.

import type { KeyboardEvent } from 'react';
import {
  describeFractionModel,
  fractionModelGrid,
  type FractionModelSpec,
} from './practice-fraction-model-data';

const INK = '#2d4a3e';
const SHADED = '#9fc5b3';
const EMPTY = '#ffffff';

// Measured in the drawing's own units, which are one pixel each when the
// picture is shown at its natural width.
const BASE_WIDTH = 300;
const GAP = 12;
const BAR_HEIGHT = 44;
const CIRCLE_SIZE = 96;
// Every bar and every rectangle is drawn the same size however many parts it
// has, so one whole always looks the same and 1/2 looks bigger than 1/5 when
// models sit side by side as answer options (22 Oct 2026).
const RECTANGLE_HEIGHT = 120;

type Shape = { x: number; y: number; width: number; height: number };

// Given where the model is the answer control for "shade the parts"
// questions (PracticeTask.tsx): every part is then a toggle the student can
// tap or reach with the keyboard, and `picked` says which are shaded.
type Tap = {
  picked: number[];
  disabled: boolean;
  onToggle: (index: number) => void;
};

// What makes one part a toggle button. Nothing when the model is a picture.
function tapProps(tap: Tap | undefined, index: number) {
  if (!tap) return {};
  return {
    role: 'button',
    tabIndex: tap.disabled ? -1 : 0,
    'aria-pressed': tap.picked.includes(index),
    'aria-label': `Part ${String(index + 1)}`,
    'aria-disabled': tap.disabled || undefined,
    className:
      'cursor-pointer focus-visible:outline-3 focus-visible:outline-forest-600',
    onClick: () => {
      if (!tap.disabled) tap.onToggle(index);
    },
    onKeyDown: (event: KeyboardEvent<SVGGElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      if (!tap.disabled) tap.onToggle(index);
    },
  };
}

// A dot in the middle of a shaded part.
function Dot({ cx, cy }: { cx: number; cy: number }) {
  return <circle cx={cx} cy={cy} r="2.2" fill={INK} />;
}

function Bar({
  shape,
  rows,
  columns,
  shaded,
  tap,
}: {
  shape: Shape;
  rows: number;
  columns: number;
  shaded: number;
  tap?: Tap | undefined;
}) {
  const cellWidth = shape.width / columns;
  const cellHeight = shape.height / rows;
  return (
    <g>
      {Array.from({ length: rows * columns }, (_, index) => {
        const x = shape.x + (index % columns) * cellWidth;
        const y = shape.y + Math.floor(index / columns) * cellHeight;
        const isShaded = tap ? tap.picked.includes(index) : index < shaded;
        return (
          <g
            key={index}
            data-part={isShaded ? 'shaded' : 'empty'}
            {...tapProps(tap, index)}
          >
            <rect
              x={x}
              y={y}
              width={cellWidth}
              height={cellHeight}
              fill={isShaded ? SHADED : EMPTY}
              stroke={INK}
              strokeWidth="1.5"
            />
            {isShaded && <Dot cx={x + cellWidth / 2} cy={y + cellHeight / 2} />}
          </g>
        );
      })}
    </g>
  );
}

// Sectors start at 12 o'clock and go clockwise.
function Circle({
  shape,
  parts,
  shaded,
  tap,
}: {
  shape: Shape;
  parts: number;
  shaded: number;
  tap?: Tap | undefined;
}) {
  const radius = shape.width / 2;
  const cx = shape.x + radius;
  const cy = shape.y + radius;
  const point = (turn: number, reach: number) => {
    const angle = 2 * Math.PI * turn - Math.PI / 2;
    return {
      x: cx + reach * Math.cos(angle),
      y: cy + reach * Math.sin(angle),
    };
  };
  const fixed = (value: number) => value.toFixed(2);
  return (
    <g>
      {Array.from({ length: parts }, (_, index) => {
        const from = point(index / parts, radius);
        const to = point((index + 1) / parts, radius);
        const middle = point((index + 0.5) / parts, radius * 0.62);
        const isShaded = tap ? tap.picked.includes(index) : index < shaded;
        return (
          <g
            key={index}
            data-part={isShaded ? 'shaded' : 'empty'}
            {...tapProps(tap, index)}
          >
            <path
              d={`M ${fixed(cx)} ${fixed(cy)} L ${fixed(from.x)} ${fixed(from.y)} A ${fixed(radius)} ${fixed(radius)} 0 0 1 ${fixed(to.x)} ${fixed(to.y)} Z`}
              fill={isShaded ? SHADED : EMPTY}
              stroke={INK}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            {isShaded && <Dot cx={middle.x} cy={middle.y} />}
          </g>
        );
      })}
    </g>
  );
}

export function FractionModelView({
  model,
  picked = [],
  disabled = false,
  onToggle,
}: {
  model: FractionModelSpec;
  // The places of the parts the student has shaded, counted from 0.
  picked?: number[];
  disabled?: boolean;
  // Given only where the model is the answer control. A model with several
  // shapes is never one.
  onToggle?: ((index: number) => void) | undefined;
}) {
  const tap: Tap | undefined =
    onToggle && model.shaded.length === 1
      ? { picked, disabled, onToggle }
      : undefined;
  const grid =
    model.shape === 'rectangle'
      ? fractionModelGrid(model.parts)
      : { rows: 1, columns: model.parts };
  const shapeWidth = model.shape === 'circle' ? CIRCLE_SIZE : BASE_WIDTH;
  const shapeHeight =
    model.shape === 'circle'
      ? CIRCLE_SIZE
      : model.shape === 'bar'
        ? BAR_HEIGHT
        : RECTANGLE_HEIGHT;
  // Shapes sit side by side and wrap onto another row when they run out of
  // room, every row holding the same number.
  const perRow = Math.max(
    1,
    Math.min(
      model.shaded.length,
      Math.floor((BASE_WIDTH + GAP) / (shapeWidth + GAP)),
    ),
  );
  const rowCount = Math.ceil(model.shaded.length / perRow);
  const width = perRow * shapeWidth + (perRow - 1) * GAP;
  const height = rowCount * shapeHeight + (rowCount - 1) * GAP;
  const pad = 2;

  return (
    <figure className="m-0 max-w-md">
      <svg
        role={tap ? 'group' : 'img'}
        aria-label={
          tap
            ? describeFractionModel({ ...model, shaded: [0] }).replace(
                ' No parts are shaded.',
                '',
              )
            : describeFractionModel(model)
        }
        viewBox={`${String(-pad)} ${String(-pad)} ${String(width + 2 * pad)} ${String(height + 2 * pad)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String((width + 2 * pad) * 1.4)}px` }}
      >
        {model.shaded.map((shaded, index) => {
          const shape: Shape = {
            x: (index % perRow) * (shapeWidth + GAP),
            y: Math.floor(index / perRow) * (shapeHeight + GAP),
            width: shapeWidth,
            height: shapeHeight,
          };
          return (
            <g key={index} data-shape={model.shape}>
              {model.shape === 'circle' ? (
                <Circle
                  shape={shape}
                  parts={model.parts}
                  shaded={shaded}
                  tap={tap}
                />
              ) : (
                <Bar
                  shape={shape}
                  rows={grid.rows}
                  columns={grid.columns}
                  shaded={shaded}
                  tap={tap}
                />
              )}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
